import { useCallback, useEffect, useRef, useState } from "react";
import { getGhgDatabaseList } from "./ghgDatabasesApi";

export const LIST_ERROR = "Couldn't load the GHG databases.";

/**
 * The GHG database list. The blocking spinner shows only on the first load;
 * `reload` refreshes in the background after a save, and a response that
 * arrives after a newer request is dropped.
 *
 * @returns {{ databases: import("./helpers").GhgDatabase[], loading: boolean, error: string, reload: () => Promise<void> }}
 */
export function useGhgDatabaseList() {
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);

  const reload = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setError("");
    try {
      const res = await getGhgDatabaseList();
      if (fetchId !== fetchIdRef.current) return;
      const rows = res?.isSuccess === false ? null : res?.data?.data;
      if (Array.isArray(rows)) setDatabases(rows);
      else setError(res?.data?.error || LIST_ERROR);
    } catch {
      if (fetchId === fetchIdRef.current) setError(LIST_ERROR);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [reload]);

  return { databases, loading, error, reload };
}
